import React, {useState} from "react";
import {BrowserRouter, Navigate, Route, Routes as RouterRoutes, useSearchParams} from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import AuthenticationGuard from "components/navigation/AuthenticationGuard";
import AdminLoginModal from "components/modals/AdminLoginModal";
import useAdminLoginShortcut from "hooks/useAdminLoginShortcut";
import NotFound from "pages/NotFound";
import AdminDashboard from './pages/admin-dashboard';
import BlogPostDetail from './pages/blog-post-detail';
import BlogListing from './pages/blog-listing';
import AdminLogin from './pages/admin-login';
import AdminPostEditor from './pages/admin-post-editor';
import AdminPostsManagement from './pages/admin-posts-management';
import AdminPostPreview from './pages/admin-post-preview';
import AdminUsersManagement from './pages/admin-users-management';
import Homepage from './pages/homepage';
import Contact from './pages/contact';
import Product from './pages/product';
import UseCases from './pages/use-cases';
import ManufacturingUseCaseDetail from './pages/manufacturing-use-case-detail';
import AutomotiveFleetManagementUseCaseDetail from './pages/automotive-fleet-management-use-case-detail';
import AutomotiveRemoteHardwareFarmUseCaseDetail from './pages/automotive-remote-hardware-farm-use-case-detail';
import MaritimeUseCaseDetail from './pages/maritime-use-case-detail';
import DefenseDualUseUseCaseDetail from './pages/defense-dual-use-use-case-detail';
import TrainingPlatformUseCaseDetail from './pages/training-platform-use-case-detail';
import About from './pages/about';
import Careers from './pages/careers';
import AdminDesignSystem from './pages/admin-design-system';

// Backward compatibility redirect component
const AdminPostPreviewRedirect = () => {
  const [searchParams] = useSearchParams();
  const postId = searchParams?.get('id');
  return postId ? <Navigate to={`/admin/posts/preview/${postId}`} replace/> :
    <Navigate to="/admin-posts-management" replace/>;
};

const RoutesContent = () => {
  const [isAdminLoginOpen, setIsAdminLoginOpen] = useState(false);

  const toggleAdminLogin = () => {
    setIsAdminLoginOpen(prev => !prev);
  };

  useAdminLoginShortcut(toggleAdminLogin);

  return (
    <>
      <ScrollToTop/>
      <AuthenticationGuard>
        <RouterRoutes>
          {/* Define your route here */}
          <Route path="/" element={<Homepage/>}/>
          <Route path="/admin-dashboard" element={<AdminDashboard/>}/>

          {/* Public blog routes */}
          <Route path="/blog/:slug" element={<BlogPostDetail/>}/>
          <Route path="/blog-post-detail" element={<BlogPostDetail/>}/>
          <Route path="/blog-listing" element={<BlogListing/>}/>

          {/* Admin routes */}
          <Route path="/admin-login" element={<AdminLogin/>}/>
          <Route path="/admin/posts/new" element={
            <AuthenticationGuard>
              <AdminPostEditor/>
            </AuthenticationGuard>
          }/>

          <Route path="/admin/posts/:id" element={
            <AuthenticationGuard>
              <AdminPostEditor/>
            </AuthenticationGuard>
          }/>

          <Route path="/admin/posts/preview/:id" element={
            <AuthenticationGuard>
              <AdminPostPreview/>
            </AuthenticationGuard>
          }/>
          <Route path="/admin-posts-management" element={<AdminPostsManagement/>}/>

          {/* Admin user management routes */}
          <Route path="/admin/users" element={<AdminUsersManagement/>}/>
          <Route path="/admin-users-management" element={<AdminUsersManagement/>}/>

          {/* Admin design system route */}
          <Route
            path="/admin/design-system"
            element={
              <AuthenticationGuard>
                <AdminDesignSystem/>
              </AuthenticationGuard>
            }
          />

          {/* Backward compatibility redirects */}
          <Route path="/admin-post-preview" element={<AdminPostPreviewRedirect/>}/>
          <Route path="/admin-post-editor" element={<AdminPostEditor/>}/>

          <Route path="/homepage" element={<Homepage/>}/>
          <Route path="/product" element={<Product/>}/>
          <Route path="/use-cases" element={<UseCases/>}/>
          <Route path="/use-cases/manufacturing" element={<ManufacturingUseCaseDetail/>}/>
          <Route path="/use-cases/automotive-fleet-management"
                 element={<AutomotiveFleetManagementUseCaseDetail/>}/>
          <Route path="/use-cases/automotive-remote-hardware-farm"
                 element={<AutomotiveRemoteHardwareFarmUseCaseDetail/>}/>
          <Route path="/use-cases/maritime" element={<MaritimeUseCaseDetail/>}/>
          <Route path="/use-cases/defense-dual-use" element={<DefenseDualUseUseCaseDetail/>}/>
          <Route path="/use-cases/training-platform" element={<TrainingPlatformUseCaseDetail/>}/>
          {/*Component not needed*/}
          {/*<Route path="/documentation" element={<Documentation/>}/>*/}
          <Route path="/about" element={<About/>}/>
          <Route path="/careers" element={<Careers/>}/>
          <Route path="/contact" element={<Contact/>}/>
          <Route path="*" element={<NotFound/>}/>
        </RouterRoutes>
      </AuthenticationGuard>
      <AdminLoginModal isOpen={isAdminLoginOpen} onClose={() => setIsAdminLoginOpen(false)}/>
    </>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <RoutesContent/>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
